import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface SymptomLog {
  id?: string;
  symptomType: string;
  intensity: number;
  time: string;
}

interface SymptomEpisodes {
  [symptomType: string]: SymptomLog[];
}

export const useSymptomTracker = (date: Date) => {
  const [symptomEpisodes, setSymptomEpisodes] = useState<SymptomEpisodes>({});
  const [hotFlashTimes, setHotFlashTimes] = useState<string[]>([]);
  const [dailyNote, setDailyNote] = useState('');
  const [dailyTriggers, setDailyTriggers] = useState<string[]>([]);
  const [symptomTriggers, setSymptomTriggers] = useState<Map<string, string[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [datesWithLogs, setDatesWithLogs] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const dateString = format(date, 'yyyy-MM-dd');

  useEffect(() => {
    loadTrackerData();
  }, [dateString]);

  useEffect(() => {
    prefetchCalendarData();
  }, []);

  const prefetchCalendarData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoString = format(thirtyDaysAgo, 'yyyy-MM-dd');

      // Fetch all dates with symptom logs in the last 30 days
      const { data: symptoms } = await supabase
        .from('symptom_logs')
        .select('log_date')
        .eq('user_id', user.id)
        .gte('log_date', thirtyDaysAgoString);

      // Fetch all dates with hot flashes
      const { data: hotFlashes } = await supabase
        .from('hot_flash_times')
        .select('log_date')
        .eq('user_id', user.id)
        .gte('log_date', thirtyDaysAgoString);

      // Fetch all dates with notes
      const { data: notes } = await supabase
        .from('daily_notes')
        .select('log_date')
        .eq('user_id', user.id)
        .gte('log_date', thirtyDaysAgoString);

      // Combine all dates with any data
      const datesSet = new Set<string>();
      symptoms?.forEach(s => datesSet.add(s.log_date));
      hotFlashes?.forEach(h => datesSet.add(h.log_date));
      notes?.forEach(n => datesSet.add(n.log_date));

      setDatesWithLogs(datesSet);
    } catch (error) {
      console.error('Error prefetching calendar data:', error);
    }
  };

  const loadTrackerData = async () => {
    // Don't clear existing data immediately - keep it while loading
    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load symptom logs
      const { data: symptoms } = await supabase
        .from('symptom_logs')
        .select('*')
        .eq('user_id', user.id)
        .eq('log_date', dateString)
        .order('created_at', { ascending: true });

      const episodes: SymptomEpisodes = {};
      if (symptoms && symptoms.length > 0) {
        symptoms.forEach(s => {
          if (!episodes[s.symptom_type]) {
            episodes[s.symptom_type] = [];
          }
          episodes[s.symptom_type].push({
            id: s.id,
            symptomType: s.symptom_type,
            intensity: s.intensity,
            time: new Date(s.created_at).toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            })
          });
        });
      }
      setSymptomEpisodes(episodes);

      // Load hot flash times
      const { data: hotFlashes } = await supabase
        .from('hot_flash_times')
        .select('*')
        .eq('user_id', user.id)
        .eq('log_date', dateString)
        .order('time_of_day');

      setHotFlashTimes(hotFlashes && hotFlashes.length > 0 ? hotFlashes.map(hf => hf.time_of_day) : []);

      // Load daily note
      const { data: note } = await supabase
        .from('daily_notes')
        .select('*')
        .eq('user_id', user.id)
        .eq('log_date', dateString)
        .maybeSingle();

      setDailyNote(note?.notes || '');

      // Load daily triggers (both general and symptom-specific)
      const { data: triggers } = await supabase
        .from('daily_triggers')
        .select('trigger_type, symptom_log_id')
        .eq('user_id', user.id)
        .eq('log_date', dateString);

      // Separate general triggers from symptom-specific ones
      const generalTriggers: string[] = [];
      const symptomSpecificTriggers = new Map<string, string[]>();
      
      triggers?.forEach(t => {
        if (t.symptom_log_id) {
          const existing = symptomSpecificTriggers.get(t.symptom_log_id) || [];
          symptomSpecificTriggers.set(t.symptom_log_id, [...existing, t.trigger_type]);
        } else {
          generalTriggers.push(t.trigger_type);
        }
      });

      setDailyTriggers(generalTriggers);
      setSymptomTriggers(symptomSpecificTriggers);
    } catch (error) {
      console.error('Error loading tracker data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTrackerData = async () => {
    console.log('[Tracker] saveTrackerData start');
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: 'Error',
          description: 'You must be logged in to save',
          variant: 'destructive'
        });
        console.warn('[Tracker] No user - aborting save');
        return;
      }

      console.log('[Tracker] Symptom episodes already saved individually');

      // Delete old hot flash times and insert new ones
      console.log('[Tracker] Replace hot flash times', { count: hotFlashTimes.length });
      await supabase
        .from('hot_flash_times')
        .delete()
        .eq('user_id', user.id)
        .eq('log_date', dateString)
        .throwOnError();

      if (hotFlashTimes.length > 0) {
        await supabase
          .from('hot_flash_times')
          .insert(
            hotFlashTimes.map((time) => ({
              user_id: user.id,
              log_date: dateString,
              time_of_day: time,
            }))
          )
          .throwOnError();
      }

      // Save daily note
      if (dailyNote.trim()) {
        console.log('[Tracker] Upsert daily note');
        await supabase
          .from('daily_notes')
          .upsert(
            {
              user_id: user.id,
              log_date: dateString,
              notes: dailyNote,
            },
            {
              onConflict: 'user_id,log_date',
            }
          )
          .throwOnError();
      }

      // Save daily triggers (only general ones without symptom_log_id)
      console.log('[Tracker] Save general triggers', { count: dailyTriggers.length });
      // Delete only general triggers (where symptom_log_id is null)
      await supabase
        .from('daily_triggers')
        .delete()
        .eq('user_id', user.id)
        .eq('log_date', dateString)
        .is('symptom_log_id', null)
        .throwOnError();

      // Insert new general triggers
      if (dailyTriggers.length > 0) {
        await supabase
          .from('daily_triggers')
          .insert(
            dailyTriggers.map((trigger) => ({
              user_id: user.id,
              log_date: dateString,
              trigger_type: trigger,
              symptom_log_id: null,
            }))
          )
          .throwOnError();
      }

      toast({
        title: 'Saved!',
        description: 'Your tracker data has been saved',
      });
      // Ensure calendar shows this day as having logs
      setDatesWithLogs((prev) => {
        const next = new Set(prev);
        next.add(dateString);
        return next;
      });
      console.log('[Tracker] saveTrackerData complete');
    } catch (error: any) {
      console.error('[Tracker] Error saving', error);
      toast({
        title: 'Error saving',
        description: error.message ?? 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
      console.log('[Tracker] saving=false');
    }
  };

  const addSymptomEpisode = async (symptomType: string, intensity: number, customTime?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to add episodes",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('symptom_logs')
        .insert({
          user_id: user.id,
          log_date: dateString,
          symptom_type: symptomType,
          intensity: intensity
        })
        .select()
        .single();

      if (error) throw error;

      // Use custom time if provided, otherwise use current time
      let displayTime: string;
      if (customTime) {
        // Convert HH:mm (24-hour) format to "HH:MM AM/PM" format
        const [hours, minutes] = customTime.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        displayTime = `${displayHour}:${minutes} ${ampm}`;
      } else {
        displayTime = new Date().toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      }

      const newEpisode: SymptomLog = {
        id: data.id,
        symptomType,
        intensity,
        time: displayTime
      };

      setSymptomEpisodes(prev => ({
        ...prev,
        [symptomType]: [...(prev[symptomType] || []), newEpisode]
      }));

      // Update calendar dots
      setDatesWithLogs(prev => new Set(prev).add(dateString));

      toast({
        title: "Episode recorded",
        description: "Symptom episode added successfully",
        className: "bg-green-50 border-green-200 text-green-900",
      });
    } catch (error) {
      console.error('Error adding symptom episode:', error);
      toast({
        title: "Error",
        description: "Failed to add symptom episode",
        variant: "destructive",
      });
    }
  };

  const deleteSymptomEpisode = async (symptomType: string, episodeId: string) => {
    try {
      // First delete associated triggers
      const { error: triggerError } = await supabase
        .from('daily_triggers')
        .delete()
        .eq('symptom_log_id', episodeId);

      if (triggerError) {
        console.error('Error deleting associated triggers:', triggerError);
      }

      // Then delete the symptom log
      const { error } = await supabase
        .from('symptom_logs')
        .delete()
        .eq('id', episodeId);

      if (error) throw error;

      // Update local state
      setSymptomEpisodes(prev => ({
        ...prev,
        [symptomType]: prev[symptomType].filter(ep => ep.id !== episodeId)
      }));

      // Remove triggers from local state
      setSymptomTriggers(prev => {
        const next = new Map(prev);
        next.delete(episodeId);
        return next;
      });

      toast({
        title: "Episode deleted",
        description: "Symptom episode and associated triggers removed successfully",
      });
    } catch (error) {
      console.error('Error deleting symptom episode:', error);
      toast({
        title: "Error",
        description: "Failed to delete symptom episode",
        variant: "destructive",
      });
    }
  };

  const addSymptomTrigger = async (symptomLogId: string, triggerType: string, logDate?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Use provided logDate or fall back to current dateString
      const targetDate = logDate || dateString;

      // Check if trigger already exists to avoid duplicates
      const existing = symptomTriggers.get(symptomLogId) || [];
      if (existing.includes(triggerType)) {
        toast({
          title: "Already added",
          description: "This trigger is already recorded for this episode",
        });
        return;
      }

      await supabase
        .from('daily_triggers')
        .insert({
          user_id: user.id,
          log_date: targetDate,
          trigger_type: triggerType,
          symptom_log_id: symptomLogId,
        })
        .throwOnError();

      // Update local state
      setSymptomTriggers(prev => {
        const next = new Map(prev);
        const existing = next.get(symptomLogId) || [];
        next.set(symptomLogId, [...existing, triggerType]);
        return next;
      });

      toast({
        title: "Trigger added",
        description: "Symptom trigger recorded",
      });
    } catch (error: any) {
      console.error('Error adding symptom trigger:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add trigger",
        variant: "destructive",
      });
    }
  };

  const removeSymptomTrigger = async (symptomLogId: string, triggerType: string, logDate?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Use provided logDate or fall back to current dateString
      const targetDate = logDate || dateString;

      await supabase
        .from('daily_triggers')
        .delete()
        .eq('user_id', user.id)
        .eq('log_date', targetDate)
        .eq('trigger_type', triggerType)
        .eq('symptom_log_id', symptomLogId)
        .throwOnError();

      // Update local state
      setSymptomTriggers(prev => {
        const next = new Map(prev);
        const existing = next.get(symptomLogId) || [];
        next.set(symptomLogId, existing.filter(t => t !== triggerType));
        return next;
      });

      toast({
        title: "Trigger removed",
        description: "Symptom trigger deleted",
      });
    } catch (error) {
      console.error('Error removing symptom trigger:', error);
      toast({
        title: "Error",
        description: "Failed to remove trigger",
        variant: "destructive",
      });
    }
  };

  const clearAllTriggers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Delete all triggers for this date (both symptom-specific and daily triggers)
      const { error } = await supabase
        .from('daily_triggers')
        .delete()
        .eq('user_id', user.id)
        .eq('log_date', dateString);

      if (error) throw error;

      // Clear local state
      setDailyTriggers([]);
      setSymptomTriggers(new Map());

      toast({
        title: "Triggers cleared",
        description: "All triggers for this date have been removed",
      });
    } catch (error) {
      console.error('Error clearing triggers:', error);
      toast({
        title: "Error",
        description: "Failed to clear triggers",
        variant: "destructive",
      });
    }
  };

  return {
    symptomEpisodes,
    addSymptomEpisode,
    deleteSymptomEpisode,
    hotFlashTimes,
    setHotFlashTimes,
    dailyNote,
    setDailyNote,
    dailyTriggers,
    setDailyTriggers,
    symptomTriggers,
    addSymptomTrigger,
    removeSymptomTrigger,
    clearAllTriggers,
    loading,
    saving,
    saveTrackerData,
    datesWithLogs
  };
};
